import React, { useEffect, useState } from 'react';
import Header from './header';
import Sidebar from './sidebar';
import { MapContainer, TileLayer, Polygon, Tooltip, useMap } from 'react-leaflet';
import { Select, Button } from 'antd';
import { ReloadOutlined, MenuOutlined } from '@ant-design/icons';
import 'leaflet/dist/leaflet.css';
import './LocalArea.css';

const { Option } = Select;

// Component to update map view when area changes
const ChangeMapView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center.length === 2) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
};

// Component to fit map bounds to show all polygons
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

export const LocalArea = () => {
  const [localAreaData, setLocalAreaData] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Fetch data function
  const fetchLocalAreaData = () => {
    setRefreshing(true);
    fetch("https://ward3-chandrapur-api.palikaapp.com/api/local-area/all")
      .then(response => response.json())
      .then(data => {
        console.log("Fetched data:", data);
        setLocalAreaData(data);
        setLoading(false);
        setRefreshing(false);
      })
      .catch(error => {
        console.error("Error fetching local area data:", error);
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    fetchLocalAreaData();
  }, []);

  // Calculate the center of a polygon
  const getPolygonCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return [27.717245, 85.32396];
    
    const latSum = coordinates.reduce((sum, coord) => sum + parseFloat(coord.longitude), 0);
    const lngSum = coordinates.reduce((sum, coord) => sum + parseFloat(coord.latitude), 0);
    
    return [
      latSum / coordinates.length,
      lngSum / coordinates.length,
    ];
  };

  // Calculate bounds to fit all areas
  const getAllAreasBounds = () => {
    if (!localAreaData || localAreaData.length === 0) return null;
    
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    
    localAreaData.forEach(area => {
      area.localAreaLanLatDTOS?.forEach(coord => {
        const lat = parseFloat(coord.longitude); // Swapped
        const lng = parseFloat(coord.latitude);  // Swapped
        
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      });
    });
    
    return [[minLat, minLng], [maxLat, maxLng]];
  };

  const mapCenter = selectedArea && selectedArea.localAreaLanLatDTOS
    ? getPolygonCenter(selectedArea.localAreaLanLatDTOS)
    : [27.717245, 85.32396];

  const allAreasBounds = getAllAreasBounds();

  const handleAreaChange = (value) => {
    if (value === 'all') {
      setSelectedArea(null);
    } else {
      const area = localAreaData.find((area) => area.id === value);
      setSelectedArea(area);
      console.log("Selected area:", area);
    }
  };

  const handleRefresh = () => {
    setSelectedArea(null);
    fetchLocalAreaData();
  };

  // Convert coordinates to Leaflet format [lat, lng]
  const getPolygonPath = (coordinates) => {
    if (!coordinates) return [];
    return coordinates.map((coord) => [
      parseFloat(coord.longitude),  // This is actually latitude (27.x)
      parseFloat(coord.latitude),   // This is actually longitude (85.x)
    ]);
  };

  // Generate random color for each area
  const getColorForArea = (index) => {
    const colors = [
      { stroke: '#FF5722', fill: '#FF9800' },
      { stroke: '#2E7D32', fill: '#4CAF50' },
      { stroke: '#1976D2', fill: '#2196F3' },
      { stroke: '#7B1FA2', fill: '#9C27B0' },
      { stroke: '#C62828', fill: '#F44336' },
      { stroke: '#00796B', fill: '#009688' },
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      
      {/* Mobile Menu Button */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="fixed top-20 left-4 z-40 lg:hidden bg-blue-600 text-white p-3 rounded-lg shadow-lg hover:bg-blue-700 transition-colors"
        >
          <MenuOutlined className="text-xl" />
        </button>
      )}

      {/* Sidebar - conditional rendering based on mobile state */}
      {(sidebarOpen || !isMobile) && (
        <Sidebar isMobile={isMobile} toggleSidebar={toggleSidebar} />
      )}

      {/* Main Content */}
      <div className={`min-h-screen bg-gray-100 transition-all duration-300 ${
        isMobile ? 'ml-0' : 'ml-64'
      } pt-20`}>
        <div className="container mx-auto px-4 py-6 max-w-7xl">
          <div className="bg-white rounded-lg shadow-lg p-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">
                विभिन्न क्षेत्रहरु
              </h1>
              <Button
                type="primary"
                icon={<ReloadOutlined spin={refreshing} />}
                onClick={handleRefresh}
                loading={refreshing}
                size="large"
                className="w-full lg:w-auto"
              >
                {refreshing ? 'लोड हुँदैछ...' : 'रिफ्रेस गर्नुहोस्'}
              </Button>
            </div>

            {/* Dropdown Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                क्षेत्र छान्नुहोस्
              </label>
              <Select
                className="w-full"
                placeholder="सबै क्षेत्रहरू"
                onChange={handleAreaChange}
                value={selectedArea ? selectedArea.id : 'all'}
                size="large"
              >
                <Option value="all">सबै क्षेत्रहरू देखाउनुहोस्</Option>
                {localAreaData.map((area) => (
                  <Option key={area.id} value={area.id}>
                    {area.name}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Area Info Cards - Show only when specific area is selected */}
            {selectedArea ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600">क्षेत्रको नाम</p>
                  <p className="text-lg font-semibold text-gray-800">{selectedArea.name}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600">क्षेत्रफल</p>
                  <p className="text-lg font-semibold text-gray-800">{selectedArea.area}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-600">वडा नम्बर</p>
                  <p className="text-lg font-semibold text-gray-800">{selectedArea.munWardId}</p>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-center text-blue-800 font-medium">
                  सबै क्षेत्रहरू नक्सामा देखाइएको छ
                </p>
              </div>
            )}

            {/* Map Container */}
            <div 
              className="w-full rounded-lg shadow-lg overflow-hidden border-2 border-gray-300" 
              style={{ height: isMobile ? '400px' : '650px' }}
            >
              {localAreaData.length > 0 ? (
                <MapContainer
                  center={mapCenter}
                  zoom={12}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                  zoomControl={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {/* Show all areas or selected area */}
                  {selectedArea ? (
                    <>
                      <ChangeMapView center={mapCenter} zoom={14} />
                      <Polygon
                        positions={getPolygonPath(selectedArea.localAreaLanLatDTOS)}
                        pathOptions={{
                          color: '#FF5722',
                          fillColor: '#FF9800',
                          fillOpacity: 0.4,
                          weight: 3,
                        }}
                      >
                        <Tooltip permanent direction="center" className="custom-tooltip">
                          <div className="font-bold text-sm">
                            {selectedArea.name}
                          </div>
                        </Tooltip>
                      </Polygon>
                    </>
                  ) : (
                    <>
                      <FitBounds bounds={allAreasBounds} />
                      {localAreaData.map((area, index) => {
                        const colors = getColorForArea(index);
                        return (
                          <Polygon
                            key={area.id}
                            positions={getPolygonPath(area.localAreaLanLatDTOS)}
                            pathOptions={{
                              color: colors.stroke,
                              fillColor: colors.fill,
                              fillOpacity: 0.35,
                              weight: 2,
                            }}
                            eventHandlers={{
                              click: () => {
                                setSelectedArea(area);
                              },
                            }}
                          >
                            <Tooltip permanent direction="center" className="custom-tooltip">
                              <div className="font-semibold text-xs">
                                {area.name}
                              </div>
                            </Tooltip>
                          </Polygon>
                        );
                      })}
                    </>
                  )}
                </MapContainer>
              ) : (
                <div className="flex items-center justify-center h-full bg-gray-50">
                  <p className="text-gray-500 text-lg">कुनै नक्सा डाटा उपलब्ध छैन</p>
                </div>
              )}
            </div>

            {/* Coordinates Info */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              {selectedArea ? (
                <div className="space-y-2">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-2 text-sm">
                    <span className="text-gray-600">
                      कुल बिन्दुहरू: <strong className="text-gray-800">{selectedArea.localAreaLanLatDTOS?.length || 0}</strong>
                    </span>
                    <span className="text-gray-600">
                      केन्द्र: <strong className="text-gray-800">
                        {mapCenter[0].toFixed(6)}, {mapCenter[1].toFixed(6)}
                      </strong>
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    टोल ID: <strong className="text-gray-800">{selectedArea.munToleId}</strong>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {localAreaData.map((area, index) => {
                    const colors = getColorForArea(index);
                    return (
                      <div 
                        key={area.id} 
                        className="flex items-center space-x-2 p-3 rounded cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200"
                        onClick={() => setSelectedArea(area)}
                      >
                        <div 
                          className="w-5 h-5 rounded border-2 flex-shrink-0" 
                          style={{ 
                            backgroundColor: colors.fill, 
                            borderColor: colors.stroke 
                          }}
                        ></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">{area.name}</p>
                          <p className="text-xs text-gray-500">{area.area}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Legend when showing all areas */}
            {!selectedArea && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 font-medium mb-2">📍 सूचना:</p>
                <p className="text-sm text-blue-700">
                  नक्सामा कुनै पनि क्षेत्रमा क्लिक गर्नुहोस् विस्तृत जानकारीको लागि। वा माथिको ड्रपडाउनबाट क्षेत्र छान्नुहोस्।
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay for mobile sidebar */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default LocalArea;